<template>
    <section v-if="items.length" class="">
        <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <SectionHeader title="Amenities & Facilities" prefix="Event!" :icon="Sparkles" />

            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div v-for="(a, i) in items" :key="a.id || i" :class="[
                    'group flex items-start gap-4 rounded-2xl border bg-card/70 backdrop-blur-2xl p-5 shadow-lg transition-all',
                    a.available === false
                        ? 'border-app opacity-60'
                        : 'border-app hover:-translate-y-1 hover:shadow-brand-500/10'
                ]">
                    <div :class="[
                        'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl',
                        a.available === false ? 'bg-muted/10 text-body/80' : 'bg-brand-500/10 text-brand-500'
                    ]">
                        <component :is="resolveIcon(a.icon)" class="h-5 w-5" />
                    </div>

                    <div class="min-w-0 flex-1">
                        <h3 class="text-header font-bold leading-snug truncate"
                            :class="{ 'line-through': a.available === false }">
                            {{ a.label }}
                        </h3>
                        <p v-if="a.description" class="text-body/80 text-xs mt-1 line-clamp-2">{{ a.description }}</p>
                        <span v-if="a.available === false"
                            class="inline-block mt-1.5 text-[10px] font-bold uppercase tracking-widest text-body/80">
                            Not available
                        </span>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import {
    Sparkles, Wifi, CircleParking, Car, Utensils, Coffee, GlassWater, Accessibility,
    ShieldCheck, Baby, Dog, Camera, AirVent, BatteryCharging, Plug, Zap, Languages,
    Bus, Music, Projector, Video, Gift, Shirt, HeartPulse, FireExtinguisher, Lock, Ticket,
} from 'lucide-vue-next';
import { EventAmenity } from '~/types';
import SectionHeader from './SectionHeader.vue';

defineProps<{ items: EventAmenity[] }>();

const iconMap: Record<string, unknown> = {
    wifi: Wifi, internet: Wifi,
    parking: CircleParking, car: Car, valet: Car,
    food: Utensils, catering: Utensils, restaurant: Utensils, meals: Utensils,
    coffee: Coffee, refreshments: Coffee, drinks: GlassWater, bar: GlassWater, water: GlassWater,
    accessible: Accessibility, accessibility: Accessibility, wheelchair: Accessibility,
    security: ShieldCheck, safety: ShieldCheck,
    childcare: Baby, kids: Baby,
    pets: Dog,
    photography: Camera, photobooth: Camera,
    ac: AirVent, 'air-conditioning': AirVent,
    charging: BatteryCharging, power: Plug, outlets: Plug, electricity: Zap,
    translation: Languages, interpretation: Languages, language: Languages,
    shuttle: Bus, transport: Bus,
    music: Music, dj: Music, entertainment: Music,
    stage: Projector, projector: Projector, av: Projector,
    screen: Video, livestream: Video, recording: Video,
    swag: Gift, gifts: Gift, merchandise: Shirt, cloakroom: Shirt, coatcheck: Shirt,
    'first-aid': HeartPulse, medical: HeartPulse, 'fire-safety': FireExtinguisher,
    lockers: Lock, checkin: Ticket, registration: Ticket,
};

const resolveIcon = (key?: string): unknown => {
    if (!key) return Sparkles;
    const k = key.trim().toLowerCase().replace(/\s+/g, '-');
    return iconMap[k] || iconMap[k.replace(/-/g, '')] || Sparkles;
};
</script>

<style scoped>
.bg-card {
    background-color: rgb(var(--core-card-bg));
}

.border-app {
    border-color: rgb(var(--core-app-border));
}

.text-header {
    color: rgb(var(--core-header-text));
}

.text-muted {
    color: rgb(var(--core-muted-text));
}
</style>
